import {
  Avatar,
  AvatarGroup,
  Button,
  Icon,
  useAvatarGroupItems,
} from "@resource/atlas";
import {
  atlasCalendarAlt,
  atlasGear,
  atlasPerson,
} from "@resource/atlas/icons";
import { strings } from "@resource/common";
import { useLogEvent } from "analytics";
import clsx from "clsx";
import GuideEditorContext, {
  ProviderType,
} from "components/GuideEditor/GuideEditorContext";
import { ModalName, useModals } from "components/ModalProvider";
import { find } from "lodash";
import moment, { Moment } from "moment";
import { useContext } from "react";
import { Event, Interviewer } from "types/legacy";

type PreviewInterviewSchedulerProps = {
  candidateName?: string;
  eventTemplateId?: string;
  availabilitySlug?: string;
  interviewer?: Interviewer;
  events: Event[];
};

function PreviewInterviewScheduler({
  candidateName,
  availabilitySlug,
  eventTemplateId,
  interviewer,
  events,
}: PreviewInterviewSchedulerProps) {
  const logEvent = useLogEvent({
    component: "PreviewInterviewScheduler",
    project: [
      "Preview a guide as a candidate",
      "Manage a job's guide settings",
    ],
  });

  const editorContext = useContext(GuideEditorContext);
  const allowSchedulerManagement =
    editorContext?.type === ProviderType.JOB_ROLE_GUIDE_TEMPLATE;

  const modalManager = useModals();
  const configured = availabilitySlug && eventTemplateId && interviewer;
  const interviewerName = strings.joinNames(
    interviewer?.firstName,
    interviewer?.lastName
  );

  const eventForSchedulerEventTemplate = find(events, { eventTemplateId });

  const avatarGroupItems = useAvatarGroupItems(
    (i) =>
      eventForSchedulerEventTemplate
        ? [
            i.avatar({
              key: interviewer?.id,
              image: interviewer?.imageUrl,
              name: interviewerName,
            }),
            i.avatar({
              key: candidateName,
              name: candidateName,
            }),
          ]
        : [],
    [
      candidateName,
      eventForSchedulerEventTemplate,
      interviewer?.id,
      interviewer?.imageUrl,
      interviewerName,
    ]
  );

  if (eventForSchedulerEventTemplate) {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    let startTimeWithTimezone: Moment | undefined;
    let startTime: string | undefined;
    if (eventForSchedulerEventTemplate?.startTime) {
      startTimeWithTimezone = moment.tz(events[0].startTime, timezone);
      startTime = `${startTimeWithTimezone.format(
        "MMMM Do"
      )} at ${startTimeWithTimezone.format(
        "h:mm a"
      )} ${startTimeWithTimezone.format("z")}`;
    }

    return (
      <div className="shadow-1 rounded-md flex items-center justify-center relative mb-6">
        <div className="flex flex-col items-center gap-6 py-16 px-16">
          <div className="flex flex-col items-center gap-4">
            <AvatarGroup size="xl" items={avatarGroupItems} />

            <div className="space-y-2">
              <p className="text-body-md-heavy text-center">
                You&apos;re all set to meet with{" "}
                {strings.splitName(interviewerName).firstName ||
                  "your interviewer"}
                !
              </p>
              <h3 className="text-h3 text-center">{startTime}</h3>
            </div>
            <Button
              icon={atlasCalendarAlt}
              variant="purple"
              isGhost
              onClick={() => {
                logEvent("Reschedule Interview Clicked");
              }}
            >
              Cancel or Reschedule
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={clsx(
        {
          "border-danger-500 border-2": !configured,
          "shadow-1": configured,
        },
        "rounded-md flex items-center justify-center relative mb-6"
      )}
    >
      <div className="absolute top-[.625rem] right-4 left-4 flex justify-between items-center">
        {!configured ? (
          <p className="text-body-md text-danger-500">
            {allowSchedulerManagement
              ? "Scheduler is misconfigured."
              : "Scheduler is misconfigured. Head to the the job settings to finish setup."}
          </p>
        ) : (
          <div />
        )}
        {allowSchedulerManagement && (
          <Button
            negativeMargin
            isGhost
            icon={atlasGear}
            onClick={() => {
              logEvent("Edit Scheduler Settings Clicked");
              modalManager.open(ModalName.SCHEDULER_SETTINGS_MANAGER);
            }}
          />
        )}
      </div>
      <div className="flex flex-col items-center gap-6 py-11 px-16">
        <div className="flex flex-col items-center gap-4">
          {interviewer ? (
            <Avatar
              size="xl"
              image={interviewer.imageUrl}
              name={interviewerName}
            />
          ) : (
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-light-gray-500">
              <Icon
                content={atlasPerson}
                className="text-subtle w-6 h-6"
                size="custom"
              />
            </div>
          )}
          <h3 className="text-h3 text-center">
            It&apos;s time to schedule your upcoming interview
            {interviewer ? ` with ${interviewerName}` : ""}!
          </h3>
        </div>
        <Button
          disabled={!configured}
          icon={atlasCalendarAlt}
          variant="purple"
          onClick={() => {
            logEvent("Book Interview Clicked");
          }}
        >
          Book a time
        </Button>
      </div>
    </div>
  );
}

export default PreviewInterviewScheduler;
